import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import { ROUTES } from "src/routes/RouteConfig";
import {
  Search,
  UserCheck,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  AlertTriangle,
  Check,
  ExternalLink,
  Download,
  X,
  GraduationCap,
  Sparkles,
  ChevronRight,
  Filter,
  DollarSign,
  ShieldCheck,
  RefreshCw,
  HelpCircle,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/components/ui/tooltip";
import { useToast } from "src/components/ui/UseToast";
import { supabase } from "src/lib/supabaseClient";
import { calculateTutorStatus, getTutorStatusBadgeClasses } from "src/utils/tutorStatusUtils";
import { parseStorageUrl } from "src/utils/storageUtils";

const CV_BUCKET = "cvs";

interface EducationEntry {
  degree?: string;
  field?: string;
  institution?: string;
  year?: string;
}

interface PendingTutor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePictureUrl?: string | null;
  specializations: string[];
  experience: number;
  hasEducation: boolean;
  educationSummary: string;
  educationEntries: EducationEntry[];
  hourlyRate: number;
  submittedAt: string;
  cvSubmitted: boolean;
  cvFilePath?: string;
  bio?: string;
  verificationStatus: string;
  isLive: boolean;
  applicationStatus: string;
  applicationStatusLabel: string;
}

const toTextArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item?.name || item?.subject || "")))
      .filter(Boolean);
  }

  if (typeof value !== "string" || !value.trim()) return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return toTextArray(parsed);
  } catch {
    // Fall back to comma-separated values.
  }

  return value.split(",").map((item) => item.trim()).filter(Boolean);
};

const hasStructuredValue = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === "string" ? value.trim().length > 0 : Boolean(value);
};

const calculateExperienceYears = (experience: unknown, totalYears?: unknown): number => {
  const totalYearsNumber = Number(totalYears);
  if (Number.isFinite(totalYearsNumber) && totalYearsNumber > 0) return totalYearsNumber;
  if (typeof experience === "number") return experience;
  if (typeof experience === "string") return Number(experience) || 0;
  if (!Array.isArray(experience)) return 0;

  return experience.reduce((total, entry) => {
    const years = Number(entry?.years || entry?.duration_years || entry?.experience_years || 0);
    return total + (Number.isFinite(years) ? years : 0);
  }, 0);
};

const parseEducation = (education: unknown): { summary: string; entries: EducationEntry[] } => {
  if (!education) return { summary: "Not specified", entries: [] };

  if (Array.isArray(education)) {
    const entries: EducationEntry[] = education.map((entry) => {
      if (typeof entry === "string") return { degree: entry };
      return {
        degree: entry?.degree || entry?.level,
        field: entry?.field,
        institution: entry?.institution,
        year: entry?.year_of_completion || entry?.year,
      };
    }).filter((e) => e.degree || e.field || e.institution);

    const summary = entries
      .map((e) => [e.degree, e.field, e.institution ? `at ${e.institution}` : ""].filter(Boolean).join(" "))
      .join("; ") || "Not specified";

    return { summary, entries };
  }

  if (typeof education === "string" && education.trim()) {
    try {
      const parsed = JSON.parse(education);
      if (Array.isArray(parsed)) return parseEducation(parsed);
    } catch {
      return { summary: education, entries: [{ degree: education }] };
    }
    return { summary: education, entries: [{ degree: education }] };
  }

  return { summary: "Not specified", entries: [] };
};

const getStorageFilePath = (cvFilePath: string): { bucket: string; filePath: string } | null => {
  const parsedUrl = parseStorageUrl(cvFilePath);
  if (parsedUrl) return parsedUrl;

  const normalizedPath = cvFilePath.trim().replace(/^\/+/, "");
  if (!normalizedPath) return null;

  if (normalizedPath.startsWith(`${CV_BUCKET}/`)) {
    return {
      bucket: CV_BUCKET,
      filePath: normalizedPath.slice(CV_BUCKET.length + 1),
    };
  }

  return {
    bucket: CV_BUCKET,
    filePath: normalizedPath,
  };
};

const getFileName = (cvFilePath: string): string => {
  try {
    const path = cvFilePath.startsWith("http") ? new URL(cvFilePath).pathname : cvFilePath;
    const fileName = path.split("/").filter(Boolean).pop();
    return fileName ? decodeURIComponent(fileName) : "Tutor CV";
  } catch {
    const fileName = cvFilePath.split("/").filter(Boolean).pop();
    return fileName || "Tutor CV";
  }
};

const getStatusUpdates = (status: string): { verification_status: string; is_live: boolean } => {
  switch (status) {
    case "active":
      return { verification_status: "approved", is_live: true };
    case "approved":
      return { verification_status: "approved", is_live: false };
    case "under_review":
      return { verification_status: "under_review", is_live: false };
    case "rejected":
      return { verification_status: "rejected", is_live: false };
    case "submitted":
    default:
      return { verification_status: "submitted", is_live: false };
  }
};

const getAgingInfo = (dateStr?: string) => {
  if (!dateStr) return { label: "Recently", isOverdue: false, diffHours: 0 };
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffHours = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));
  const diffDays = Math.floor(diffHours / 24);
  const isOverdue = diffHours >= 48;

  let label = `${diffHours}h ago`;
  if (diffDays >= 1) {
    label = `${diffDays}d ago`;
  }
  return { label, isOverdue, diffHours };
};

type FilterTab = "all" | "ready" | "under_review" | "missing_cv" | "sla_risk";

const ApprovalQueue = () => {
  const navigate = useNavigate();
  const { setTitle, setDescription, reset } = usePageHeaderStore();
  const { toast } = useToast();

  const [pendingTutors, setPendingTutors] = useState<PendingTutor[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<FilterTab>("all");
  const [curriculumFilter, setCurriculumFilter] = useState("all");

  // Selection & Drawer State
  const [selectedTutorId, setSelectedTutorId] = useState<string | null>(null);
  const [checkedTutorIds, setCheckedTutorIds] = useState<Set<string>>(new Set());
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [internalNote, setInternalNote] = useState("");

  useEffect(() => {
    setTitle("Tutor Approval Queue");
    setDescription("Executive triage workbench to review credentials, verify curricula, and authorize tutor applications.");

    return () => reset();
  }, [setTitle, setDescription, reset]);

  const fetchPendingTutors = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const { data, error } = await supabase
        .from("tutors")
        .select(`
          id,
          education,
          hourly_rate,
          subjects_taught,
          experience,
          verification_status,
          is_live,
          cv_file_path,
          bio,
          created_at,
          updated_at,
          total_years_exp,
          profile:profiles!tutors_id_fkey (
            first_name,
            last_name,
            email,
            profile_picture_url
          )
        `)
        .in("verification_status", ["submitted", "under_review"])
        .order("created_at", { ascending: false });

      if (error) throw error;

      const tutors: PendingTutor[] = (data || []).map((row) => {
        const profile = Array.isArray(row.profile) ? row.profile[0] : row.profile;
        const status = calculateTutorStatus(row.verification_status, row.is_live);
        const edu = parseEducation(row.education);

        return {
          id: row.id,
          firstName: profile?.first_name || "Applicant",
          lastName: profile?.last_name || "",
          email: profile?.email || "No email on record",
          profilePictureUrl: profile?.profile_picture_url || null,
          specializations: toTextArray(row.subjects_taught),
          experience: calculateExperienceYears(row.experience, row.total_years_exp),
          hasEducation: hasStructuredValue(row.education),
          educationSummary: edu.summary,
          educationEntries: edu.entries,
          hourlyRate: Number(row.hourly_rate) || 0,
          submittedAt: row.created_at || row.updated_at,
          cvSubmitted: Boolean(row.cv_file_path),
          cvFilePath: row.cv_file_path || undefined,
          bio: row.bio || undefined,
          verificationStatus: row.verification_status,
          isLive: Boolean(row.is_live),
          applicationStatus: status.status,
          applicationStatusLabel: status.displayValue,
        };
      });

      setPendingTutors(tutors);

      // Keep selected tutor updated if drawer is open
      if (selectedTutorId && !tutors.some((t) => t.id === selectedTutorId)) {
        setIsDrawerOpen(false);
        setSelectedTutorId(null);
      }
    } catch (error) {
      console.error("Failed to fetch pending tutors:", error);
      toast({
        title: "Error Loading Queue",
        description: "Failed to retrieve pending tutor applications. Please refresh.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedTutorId, toast]);

  useEffect(() => {
    fetchPendingTutors();
  }, [fetchPendingTutors]);

  // Keyboard shortcut: Escape to close drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDrawerOpen]);

  // Selected tutor object for drawer
  const activeTutor = useMemo(() => {
    return pendingTutors.find((t) => t.id === selectedTutorId) || null;
  }, [pendingTutors, selectedTutorId]);

  // Open drawer for a tutor
  const handleOpenDrawer = (tutor: PendingTutor) => {
    setSelectedTutorId(tutor.id);
    setIsDrawerOpen(true);
  };

  // Close drawer
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  // Checkbox bulk toggle
  const toggleSelectTutor = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCheckedTutorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = (filteredList: PendingTutor[]) => {
    if (checkedTutorIds.size === filteredList.length) {
      setCheckedTutorIds(new Set());
    } else {
      setCheckedTutorIds(new Set(filteredList.map((t) => t.id)));
    }
  };

  // Update Status in Supabase (Single Tutor)
  const handleUpdateStatus = async (targetStatus: string, tutorId?: string) => {
    const targetId = tutorId || selectedTutorId;
    if (!targetId) return;

    try {
      setUpdatingStatus(true);
      const statusUpdates = getStatusUpdates(targetStatus);

      const { error } = await supabase
        .from("tutors")
        .update({
          ...statusUpdates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", targetId);

      if (error) throw error;

      const updatedDisplay = calculateTutorStatus(statusUpdates.verification_status, statusUpdates.is_live);

      // If approved or rejected or active, remove from pending queue if not matching filter
      setPendingTutors((prev) =>
        prev.map((t) => {
          if (t.id === targetId) {
            return {
              ...t,
              verificationStatus: statusUpdates.verification_status,
              isLive: statusUpdates.is_live,
              applicationStatus: updatedDisplay.status,
              applicationStatusLabel: updatedDisplay.displayValue,
            };
          }
          return t;
        }).filter((t) => ["submitted", "under_review"].includes(t.verificationStatus))
      );

      toast({
        title: "Status Updated",
        description: `Tutor record transitioned to "${updatedDisplay.displayValue}".`,
        variant: "default",
      });

      if (targetStatus === "approved" || targetStatus === "active" || targetStatus === "rejected") {
        setIsDrawerOpen(false);
        setSelectedTutorId(null);
      }
    } catch (error) {
      console.error("Failed to update status:", error);
      toast({
        title: "Status Update Failed",
        description: "Unable to update tutor application status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Bulk status update
  const handleBulkUpdateStatus = async (targetStatus: string) => {
    if (checkedTutorIds.size === 0) return;

    try {
      setUpdatingStatus(true);
      const statusUpdates = getStatusUpdates(targetStatus);
      const ids = Array.from(checkedTutorIds);

      const { error } = await supabase
        .from("tutors")
        .update({
          ...statusUpdates,
          updated_at: new Date().toISOString(),
        })
        .in("id", ids);

      if (error) throw error;

      toast({
        title: "Bulk Action Completed",
        description: `Updated ${ids.length} candidate(s) to "${targetStatus.replace("_", " ")}".`,
        variant: "default",
      });

      setCheckedTutorIds(new Set());
      await fetchPendingTutors(true);
    } catch (error) {
      console.error("Failed to update bulk status:", error);
      toast({
        title: "Bulk Action Failed",
        description: "Could not apply batch updates. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // View / Download CV
  const handleOpenCV = async (download = false) => {
    if (!activeTutor?.cvFilePath) {
      toast({
        title: "No CV on Record",
        description: "This applicant has not uploaded a CV file.",
        variant: "destructive",
      });
      return;
    }

    try {
      const storageFile = getStorageFilePath(activeTutor.cvFilePath);
      let signedUrl = activeTutor.cvFilePath;

      if (storageFile) {
        const { data, error } = await supabase.storage
          .from(storageFile.bucket)
          .createSignedUrl(storageFile.filePath, 300, download ? { download: true } : undefined);

        if (error || !data?.signedUrl) {
          throw error || new Error("Failed to generate CV link");
        }
        signedUrl = data.signedUrl;
      }

      if (download) {
        const link = document.createElement("a");
        link.href = signedUrl;
        link.download = getFileName(activeTutor.cvFilePath);
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        window.open(signedUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Failed to access CV file:", error);
      toast({
        title: "Unable to Access CV",
        description: "Could not retrieve the document file. Please verify storage permissions.",
        variant: "destructive",
      });
    }
  };

  // Filtered dataset
  const filteredTutors = useMemo(() => {
    return pendingTutors.filter((tutor) => {
      // Search match
      const query = searchQuery.trim().toLowerCase();
      const matchesSearch =
        !query ||
        tutor.firstName.toLowerCase().includes(query) ||
        tutor.lastName.toLowerCase().includes(query) ||
        tutor.email.toLowerCase().includes(query) ||
        tutor.specializations.some((spec) => spec.toLowerCase().includes(query));

      if (!matchesSearch) return false;

      // Curriculum match
      if (curriculumFilter !== "all") {
        const hasScope = tutor.specializations.some((spec) =>
          spec.toLowerCase().includes(curriculumFilter.toLowerCase())
        );
        if (!hasScope) return false;
      }

      // Tab match
      const ageInfo = getAgingInfo(tutor.submittedAt);
      switch (activeTab) {
        case "ready":
          return tutor.cvSubmitted && tutor.hasEducation;
        case "under_review":
          return tutor.verificationStatus === "under_review";
        case "missing_cv":
          return !tutor.cvSubmitted;
        case "sla_risk":
          return ageInfo.isOverdue;
        case "all":
        default:
          return true;
      }
    });
  }, [pendingTutors, searchQuery, activeTab, curriculumFilter]);

  // Derived KPI aggregates
  const kpis = useMemo(() => {
    const total = pendingTutors.length;
    const underReview = pendingTutors.filter((t) => t.verificationStatus === "under_review").length;
    const submitted = pendingTutors.filter((t) => t.verificationStatus === "submitted").length;
    const missingCv = pendingTutors.filter((t) => !t.cvSubmitted).length;
    const slaRisks = pendingTutors.filter((t) => getAgingInfo(t.submittedAt).isOverdue).length;
    const readyForDecision = pendingTutors.filter((t) => t.cvSubmitted && t.hasEducation).length;

    return { total, underReview, submitted, missingCv, slaRisks, readyForDecision };
  }, [pendingTutors]);

  return (
    <div className="space-y-5">
      {/* Page Breadcrumbs */}
      <div className="flex items-center gap-1.5 text-xs text-slate-500 pb-1">
        <button
          type="button"
          onClick={() => navigate(ROUTES.ADMIN_TUTORS.path)}
          className="hover:text-purple-700 transition"
        >
          Academic Operations
        </button>
        <span>/</span>
        <button
          type="button"
          onClick={() => navigate(ROUTES.ADMIN_TUTORS_LIST.path)}
          className="hover:text-purple-700 transition"
        >
          Tutor Directory
        </button>
        <span>/</span>
        <span className="font-semibold text-slate-800">Approval Queue</span>
      </div>

      {loading ? (
        <div className="space-y-4 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
          <div className="h-96 bg-slate-100 rounded-xl animate-pulse" />
        </div>
      ) : (
        <>
          {/* KPI METRIC STRIP (No decorative fluff, tabular numerals, clean status cues) */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Metric 1: Pending First Review */}
        <div className="bg-white p-3.5 rounded-xl border border-slate-200/90 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500">Awaiting First Review</p>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="text-2xl font-bold text-slate-900 tabular-nums">{kpis.submitted}</span>
              <span className="text-xs font-medium text-amber-700 bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200">
                New Intake
              </span>
            </div>
          </div>
          <div className="w-9 h-9 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600">
            <Clock className="w-4 h-4" />
          </div>
        </div>

        {/* Metric 2: Currently Under Review */}
        <div className="bg-white p-3.5 rounded-xl border border-slate-200/90 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500">In Active Audit</p>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="text-2xl font-bold text-slate-900 tabular-nums">{kpis.underReview}</span>
              <span className="text-xs font-medium text-violet-700 bg-violet-50 px-1.5 py-0.2 rounded border border-violet-200">
                Under Review
              </span>
            </div>
          </div>
          <div className="w-9 h-9 rounded-lg bg-violet-50 border border-violet-100 flex items-center justify-center text-violet-600">
            <ShieldCheck className="w-4 h-4" />
          </div>
        </div>

        {/* Metric 3: SLA Attention Required */}
        <div className="bg-white p-3.5 rounded-xl border border-slate-200/90 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500">SLA Attention (&gt;48h)</p>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="text-2xl font-bold text-slate-900 tabular-nums">{kpis.slaRisks}</span>
              {kpis.slaRisks > 0 ? (
                <span className="text-xs font-medium text-rose-700 bg-rose-50 px-1.5 py-0.2 rounded border border-rose-200">
                  Priority Action
                </span>
              ) : (
                <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">
                  On Target
                </span>
              )}
            </div>
          </div>
          <div className="w-9 h-9 rounded-lg bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-600">
            <AlertTriangle className="w-4 h-4" />
          </div>
        </div>

        {/* Metric 4: Total Queue */}
        <div className="bg-white p-3.5 rounded-xl border border-slate-200/90 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500">Total in Queue</p>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="text-2xl font-bold text-slate-900 tabular-nums">{kpis.total}</span>
              <span className="text-xs text-slate-500 font-medium">
                {kpis.readyForDecision} ready for review
              </span>
            </div>
          </div>
          <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600">
            <UserCheck className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* FILTER & WORKBENCH TOOLBAR */}
      <div className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-xs space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          {/* Quick Filter Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pb-1 lg:pb-0 text-xs font-medium text-slate-600">
            <button
              onClick={() => setActiveTab("all")}
              className={`px-3 py-1.5 rounded-lg border transition-all flex items-center gap-1.5 whitespace-nowrap ${
                activeTab === "all"
                  ? "bg-violet-50 text-violet-900 border-violet-200 font-semibold"
                  : "bg-white text-slate-600 border-transparent hover:bg-slate-100"
              }`}
            >
              <span>All in Queue</span>
              <span className="bg-slate-200/80 text-slate-800 px-1.5 py-0.2 rounded-full text-[11px] tabular-nums">
                {kpis.total}
              </span>
            </button>

            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setActiveTab("ready")}
                    className={`px-3 py-1.5 rounded-lg border transition-all flex items-center gap-1.5 whitespace-nowrap group ${
                      activeTab === "ready"
                        ? "bg-violet-50 text-violet-900 border-violet-200 font-semibold"
                        : "bg-white text-slate-600 border-transparent hover:bg-slate-100"
                    }`}
                  >
                    <span>Ready for Review</span>
                    <span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.2 rounded-full text-[11px] tabular-nums font-semibold">
                      {kpis.readyForDecision}
                    </span>
                    <HelpCircle className="w-3 h-3 text-slate-400 group-hover:text-slate-600 transition-colors" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs p-3 text-xs bg-slate-900 text-white rounded-lg shadow-xl border border-slate-800 space-y-1.5">
                  <p className="font-semibold text-slate-100">Ready for Review Criteria</p>
                  <p className="text-slate-300 text-[11px] leading-snug">
                    Candidates who have submitted both required application items:
                  </p>
                  <ul className="space-y-1 text-slate-300 text-[11px] pt-0.5">
                    <li className="flex items-start gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1 shrink-0"></span>
                      <span>
                        <strong className="text-slate-200 font-medium">CV / Resume file attached</strong> (<code className="text-emerald-300 font-mono text-[10px]">cv_file_path</code> is present)
                      </span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1 shrink-0"></span>
                      <span>
                        <strong className="text-slate-200 font-medium">Education / Degree details provided</strong> (<code className="text-emerald-300 font-mono text-[10px]">education</code> has structured entries)
                      </span>
                    </li>
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <button
              onClick={() => setActiveTab("under_review")}
              className={`px-3 py-1.5 rounded-lg border transition-all flex items-center gap-1.5 whitespace-nowrap ${
                activeTab === "under_review"
                  ? "bg-violet-50 text-violet-900 border-violet-200 font-semibold"
                  : "bg-white text-slate-600 border-transparent hover:bg-slate-100"
              }`}
            >
              <span>Under Review</span>
              <span className="bg-violet-100 text-violet-800 px-1.5 py-0.2 rounded-full text-[11px] tabular-nums">
                {kpis.underReview}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("missing_cv")}
              className={`px-3 py-1.5 rounded-lg border transition-all flex items-center gap-1.5 whitespace-nowrap text-amber-700 ${
                activeTab === "missing_cv"
                  ? "bg-amber-50 text-amber-900 border-amber-300 font-semibold"
                  : "bg-white border-transparent hover:bg-slate-100"
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
              <span>Missing CV</span>
              <span className="bg-amber-100 text-amber-800 px-1.5 py-0.2 rounded-full text-[11px] tabular-nums">
                {kpis.missingCv}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("sla_risk")}
              className={`px-3 py-1.5 rounded-lg border transition-all flex items-center gap-1.5 whitespace-nowrap text-rose-700 ${
                activeTab === "sla_risk"
                  ? "bg-rose-50 text-rose-900 border-rose-300 font-semibold"
                  : "bg-white border-transparent hover:bg-slate-100"
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
              <span>SLA Risk (&gt;48h)</span>
              <span className="bg-rose-100 text-rose-800 px-1.5 py-0.2 rounded-full text-[11px] tabular-nums">
                {kpis.slaRisks}
              </span>
            </button>
          </div>

          {/* Search, Scope Filter & Refresh */}
          <div className="flex items-center gap-2">
            <div className="relative w-full sm:w-64">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
              <Input
                type="text"
                placeholder="Search candidate, subject, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="text-xs pl-8 pr-3 py-1.5 h-8 bg-slate-50 border-slate-200 rounded-lg focus:ring-violet-500/20"
              />
            </div>

            <select
              value={curriculumFilter}
              onChange={(e) => setCurriculumFilter(e.target.value)}
              className="text-xs h-8 px-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-500"
            >
              <option value="all">All Curricula</option>
              <option value="math">Math & Calculus</option>
              <option value="physics">Physics</option>
              <option value="chemistry">Chemistry</option>
              <option value="biology">Biology</option>
              <option value="computer">Computer Science</option>
              <option value="english">English & Humanities</option>
            </select>

            <button
              onClick={() => fetchPendingTutors(true)}
              disabled={refreshing}
              title="Refresh queue"
              className="p-1.5 text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin text-violet-600" : ""}`} />
            </button>
          </div>
        </div>

        {/* BULK ACTION BAR (Visible when any checkbox is toggled) */}
        {checkedTutorIds.size > 0 && (
          <div className="flex items-center justify-between text-xs bg-slate-900 text-white px-3.5 py-2 rounded-lg transition-all animate-fadeIn">
            <div className="flex items-center gap-2.5">
              <span className="font-medium">
                <strong className="text-violet-300 tabular-nums">{checkedTutorIds.size}</strong> candidate(s) selected
              </span>
              <button
                onClick={() => setCheckedTutorIds(new Set())}
                className="text-slate-400 hover:text-white underline text-[11px]"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={updatingStatus}
                onClick={() => handleBulkUpdateStatus("under_review")}
                className="h-7 text-xs bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
              >
                Mark Under Review
              </Button>
              <Button
                size="sm"
                disabled={updatingStatus}
                onClick={() => handleBulkUpdateStatus("approved")}
                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              >
                Authorize & Mark Ready
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* WORKBENCH TABLE CONTAINER */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-600 border-collapse">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider text-[11px]">
                <th className="py-3 px-3.5 w-8">
                  <input
                    type="checkbox"
                    checked={filteredTutors.length > 0 && checkedTutorIds.size === filteredTutors.length}
                    onChange={() => handleSelectAll(filteredTutors)}
                    className="rounded border-slate-300 text-violet-600 focus:ring-0"
                  />
                </th>
                <th className="py-3 px-4 font-semibold">Tutor Candidate</th>
                <th className="py-3 px-4 font-semibold">Subjects & Curricula</th>
                <th className="py-3 px-3 font-semibold text-center">Exp.</th>
                <th className="py-3 px-4 font-semibold">Verification Audit</th>
                <th className="py-3 px-3 font-semibold text-right">Rate</th>
                <th className="py-3 px-4 font-semibold">Waiting SLA</th>
                <th className="py-3 px-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredTutors.map((tutor) => {
                const isSelected = tutor.id === selectedTutorId;
                const isChecked = checkedTutorIds.has(tutor.id);
                const age = getAgingInfo(tutor.submittedAt);
                const initials = `${tutor.firstName?.[0] || ""}${tutor.lastName?.[0] || ""}`.toUpperCase() || "TU";

                return (
                  <tr
                    key={tutor.id}
                    onClick={() => handleOpenDrawer(tutor)}
                    className={`transition-colors cursor-pointer ${
                      isSelected
                        ? "bg-violet-50/60 hover:bg-violet-50/80"
                        : "hover:bg-slate-50/80"
                    }`}
                  >
                    {/* Checkbox */}
                    <td className="py-3 px-3.5" onClick={(e) => toggleSelectTutor(tutor.id, e)}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {}}
                        className="rounded border-slate-300 text-violet-600 focus:ring-0"
                      />
                    </td>

                    {/* Candidate Identity */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {tutor.profilePictureUrl ? (
                          <img
                            src={tutor.profilePictureUrl}
                            alt={`${tutor.firstName} ${tutor.lastName}`}
                            className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
                            onError={(e) => {
                              (e.currentTarget as HTMLElement).style.display = "none";
                              const fallback = e.currentTarget.parentElement?.querySelector(".avatar-fallback") as HTMLElement;
                              if (fallback) fallback.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-8 h-8 rounded-full bg-slate-100 text-slate-700 font-bold items-center justify-center text-xs border border-slate-200 shrink-0 avatar-fallback ${
                            tutor.profilePictureUrl ? "hidden" : "flex"
                          }`}
                        >
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-slate-900 truncate flex items-center gap-1.5">
                            <span>{tutor.firstName} {tutor.lastName}</span>
                            {isSelected && (
                              <span className="w-1.5 h-1.5 rounded-full bg-violet-600" title="Active selection" />
                            )}
                          </div>
                          <p className="text-slate-500 text-[11px] truncate font-mono">{tutor.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Subjects & Curricula */}
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {tutor.specializations.slice(0, 2).map((spec) => (
                          <span
                            key={spec}
                            className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium text-[11px] truncate max-w-[130px]"
                          >
                            {spec}
                          </span>
                        ))}
                        {tutor.specializations.length > 2 && (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[11px]">
                            +{tutor.specializations.length - 2}
                          </span>
                        )}
                        {tutor.specializations.length === 0 && (
                          <span className="text-slate-400 text-[11px]">General Tutoring</span>
                        )}
                      </div>
                    </td>

                    {/* Experience */}
                    <td className="py-3 px-3 text-center">
                      <span className="font-semibold text-slate-800 tabular-nums">
                        {tutor.experience > 0 ? `${tutor.experience}y` : "—"}
                      </span>
                    </td>

                    {/* Verification Audit Badges */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        {tutor.cvSubmitted ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <Check className="w-3 h-3 text-emerald-600 stroke-[2.5]" />
                            CV File
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                            <X className="w-3 h-3 text-rose-500 stroke-[2.5]" />
                            No CV
                          </span>
                        )}

                        {tutor.hasEducation ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Degree On File
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
                            No Edu
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Requested Hourly Rate */}
                    <td className="py-3 px-3 text-right">
                      {tutor.hourlyRate > 0 ? (
                        <>
                          <span className="font-bold text-slate-900 tabular-nums">${tutor.hourlyRate}</span>
                          <span className="text-slate-400 text-[11px]">/hr</span>
                        </>
                      ) : (
                        <span className="text-slate-400 text-[11px]">Not set</span>
                      )}
                    </td>

                    {/* Waiting SLA */}
                    <td className="py-3 px-4">
                      {age.isOverdue ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                          {age.label} (SLA Alert)
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 font-medium">
                          <Clock className="w-3 h-3 text-slate-400" />
                          {age.label}
                        </span>
                      )}
                    </td>

                    {/* Action buttons */}
                    <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenDrawer(tutor)}
                          className="h-7 px-2.5 text-xs text-slate-700 border-slate-300 hover:bg-slate-100"
                        >
                          Inspect
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {filteredTutors.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-500">
                    <div className="max-w-xs mx-auto space-y-2">
                      <div className="w-10 h-10 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                        <UserCheck className="w-5 h-5" />
                      </div>
                      <p className="font-semibold text-slate-800 text-sm">No pending applications found</p>
                      <p className="text-xs text-slate-500">
                        {searchQuery || activeTab !== "all" || curriculumFilter !== "all"
                          ? "Try clearing filters to view all submitted applicants."
                          : "All tutor applications have been processed and authorized."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer */}
        <div className="px-4 py-3 bg-slate-50/70 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <p>
            Showing <span className="font-semibold text-slate-700 tabular-nums">{filteredTutors.length}</span> of{" "}
            <span className="font-semibold text-slate-700 tabular-nums">{pendingTutors.length}</span> candidates in queue
          </p>
          <div className="flex items-center gap-1 text-[11px]">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1" />
            <span>SLA Standard: 24h review target</span>
          </div>
        </div>
      </div>
        </>
      )}

      {/* ========================================================================= */}
      {/* DOCKED RIGHT SLIDE-OVER REVIEW DRAWER */}
      {/* ========================================================================= */}
      {isDrawerOpen && activeTutor && (
        <>
          {/* Backdrop on small screens */}
          <div
            className="fixed inset-0 bg-slate-900/30 backdrop-blur-2xs z-40 lg:hidden transition-opacity"
            onClick={handleCloseDrawer}
          />

          <div
            className="fixed inset-y-0 right-0 w-full max-w-xl bg-white shadow-2xl z-50 border-l border-slate-200 flex flex-col transform transition-transform duration-200 ease-out"
          >
            {/* Drawer Header */}
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/90">
              <div className="flex items-center gap-3 min-w-0">
                {activeTutor.profilePictureUrl ? (
                  <img
                    src={activeTutor.profilePictureUrl}
                    alt={`${activeTutor.firstName} ${activeTutor.lastName}`}
                    className="w-10 h-10 rounded-full object-cover border border-violet-200 shrink-0"
                    onError={(e) => {
                      (e.currentTarget as HTMLElement).style.display = "none";
                      const fallback = e.currentTarget.parentElement?.querySelector(".drawer-avatar-fallback") as HTMLElement;
                      if (fallback) fallback.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className={`w-10 h-10 rounded-full bg-violet-100 text-violet-700 font-bold items-center justify-center border border-violet-200 shrink-0 drawer-avatar-fallback ${
                    activeTutor.profilePictureUrl ? "hidden" : "flex"
                  }`}
                >
                  {`${activeTutor.firstName?.[0] || ""}${activeTutor.lastName?.[0] || ""}`.toUpperCase() || "TU"}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-900 text-sm truncate">
                      {activeTutor.firstName} {activeTutor.lastName}
                    </h3>
                    <span
                      className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${getTutorStatusBadgeClasses(
                        activeTutor.verificationStatus
                      )}`}
                    >
                      {activeTutor.applicationStatusLabel}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 font-mono truncate">{activeTutor.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => navigate(`/admin/tutors/view/${activeTutor.id}?from=approval-queue`)}
                  title="Open full tutor page"
                  className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-500 hover:text-slate-800 transition"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
                <button
                  onClick={handleCloseDrawer}
                  title="Close (Esc)"
                  className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-500 hover:text-slate-800 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Drawer Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5 text-xs text-slate-700">
              {/* Stepper Progress Indicator */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                  Verification Progress
                </p>
                <div className="space-y-2.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-slate-700">
                      <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold">
                        ✓
                      </span>
                      1. Profile &amp; Contact Information
                    </span>
                    <span className="text-emerald-700 font-medium">Verified</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-slate-700">
                      <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          activeTutor.specializations.length > 0
                            ? "bg-emerald-500 text-white"
                            : "bg-slate-300 text-slate-600"
                        }`}
                      >
                        {activeTutor.specializations.length > 0 ? "✓" : "2"}
                      </span>
                      2. Curriculum Scope Alignment
                    </span>
                    <span
                      className={
                        activeTutor.specializations.length > 0 ? "text-emerald-700 font-medium" : "text-slate-400"
                      }
                    >
                      {activeTutor.specializations.length > 0 ? "Provided" : "Pending"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-slate-700 font-medium">
                      <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          activeTutor.cvSubmitted && activeTutor.hasEducation
                            ? "bg-emerald-500 text-white"
                            : "bg-amber-500 text-white"
                        }`}
                      >
                        {activeTutor.cvSubmitted && activeTutor.hasEducation ? "✓" : "3"}
                      </span>
                      3. CV &amp; Credentials Audit
                    </span>
                    <span
                      className={
                        activeTutor.cvSubmitted && activeTutor.hasEducation
                          ? "text-emerald-700 font-medium"
                          : "text-amber-700 font-semibold"
                      }
                    >
                      {activeTutor.cvSubmitted && activeTutor.hasEducation ? "Verified" : "Review Required"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between opacity-60">
                    <span className="flex items-center gap-2 text-slate-500">
                      <span className="w-5 h-5 rounded-full bg-slate-300 text-slate-700 flex items-center justify-center text-[10px] font-bold">
                        4
                      </span>
                      4. Rate Lock &amp; Catalog Activation
                    </span>
                    <span className="text-slate-400">Pending Decision</span>
                  </div>
                </div>
              </div>

              {/* CV Document Preview Card */}
              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
                <div className="bg-slate-100/80 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-700 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-violet-600" />
                    CV Document Status
                  </span>
                  {activeTutor.cvSubmitted && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenCV(false)}
                        className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
                      >
                        View Full CV
                      </button>
                      <span className="text-slate-300">·</span>
                      <button
                        onClick={() => handleOpenCV(true)}
                        className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </button>
                    </div>
                  )}
                </div>

                <div className="p-4 bg-slate-50/50 space-y-3">
                  {activeTutor.cvSubmitted ? (
                    <div>
                      <p className="font-mono text-[11px] text-slate-600 break-all mb-1">
                        {getFileName(activeTutor.cvFilePath || "")}
                      </p>
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        <Check className="w-3 h-3 text-emerald-600" />
                        Uploaded &amp; Available in Storage
                      </span>
                    </div>
                  ) : (
                    <div className="bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-lg flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-semibold text-xs">Missing Resume / CV Document</p>
                        <p className="text-[11px] text-rose-700 mt-0.5">
                          Applicant submitted without an attached resume file. You can request an updated CV before granting approval.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Education Record */}
                  <div className="bg-white p-3 rounded-lg border border-slate-200 space-y-1">
                    <div className="flex items-center gap-1.5 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                      <GraduationCap className="w-3.5 h-3.5 text-slate-400" />
                      <span>Academic Credentials</span>
                    </div>
                    <p className="font-medium text-slate-900 text-xs">
                      {activeTutor.educationSummary}
                    </p>
                    {activeTutor.experience > 0 && (
                      <p className="text-[11px] text-slate-500">
                        Teaching Experience: <strong className="text-slate-800 tabular-nums">{activeTutor.experience} years</strong>
                      </p>
                    )}
                  </div>

                  {/* Bio / Teaching Philosophy */}
                  {activeTutor.bio && (
                    <div className="bg-white p-3 rounded-lg border border-slate-200">
                      <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider mb-1">
                        Bio &amp; Teaching Philosophy
                      </span>
                      <p className="text-slate-700 leading-relaxed text-xs">
                        &ldquo;{activeTutor.bio}&rdquo;
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Commercial Pricing & Margin Calculator */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5 text-slate-500" />
                    Commercial &amp; Rate Verification
                  </span>
                  {activeTutor.hourlyRate > 0 && (
                    <span className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-medium">
                      {activeTutor.hourlyRate > 60 ? "Tier A Specialist" : activeTutor.hourlyRate > 40 ? "Standard Tier" : "Introductory Tier"}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-[10px] text-slate-400 uppercase font-medium">Tutor Rate</p>
                    <p className="text-base font-bold text-slate-900 tabular-nums mt-0.5">
                      {activeTutor.hourlyRate > 0 ? `$${activeTutor.hourlyRate}.00` : "—"}
                    </p>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-[10px] text-slate-400 uppercase font-medium">Student Price</p>
                    <p className="text-base font-bold text-slate-900 tabular-nums mt-0.5">
                      {activeTutor.hourlyRate > 0 ? `$${Math.round(activeTutor.hourlyRate * 1.45)}.00` : "—"}
                    </p>
                  </div>
                  <div className="p-2.5 bg-violet-50 rounded-lg border border-violet-200">
                    <p className="text-[10px] text-violet-700 uppercase font-medium">Gross Margin</p>
                    <p className="text-base font-bold text-violet-900 tabular-nums mt-0.5">
                      {activeTutor.hourlyRate > 0 ? "31.0%" : "—"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Internal Audit Note */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700">
                  Verification Note / Decision Rationale
                </label>
                <textarea
                  rows={2}
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  placeholder="e.g. Verified university degree and CV. Qualified for High Dosage Tutoring scope."
                  className="w-full text-xs p-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-500"
                />
              </div>
            </div>

            {/* Drawer Sticky Action Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
              <Button
                variant="outline"
                size="sm"
                disabled={updatingStatus}
                onClick={() => handleUpdateStatus("rejected")}
                className="text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
              >
                Reject
              </Button>

              <div className="flex items-center gap-2">
                {activeTutor.verificationStatus === "submitted" && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={updatingStatus}
                    onClick={() => handleUpdateStatus("under_review")}
                    className="text-xs text-slate-700 border-slate-300 hover:bg-slate-100"
                  >
                    Mark Under Review
                  </Button>
                )}

                <Button
                  size="sm"
                  disabled={updatingStatus}
                  onClick={() => handleUpdateStatus("approved")}
                  className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-1.5 shadow-xs"
                >
                  <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                  Approve &amp; Mark Ready
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ApprovalQueue;
